import React, { useState, useEffect, useRef } from 'react';
import { 
  IonContent, 
  IonHeader, 
  IonPage, 
  IonTitle, 
  IonToolbar, 
  IonButtons, 
  IonBackButton,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonSearchbar,
  IonList,
  IonItem,
  IonIcon,
  IonFab,
  IonFabButton,
  IonFabList,
  IonCard,
  IonCardContent,
  IonButton,
  IonText,
  useIonRouter,
  useIonViewDidEnter,
  useIonViewWillLeave,
  IonModal,
  IonCardHeader,
  useIonAlert,
  IonLoading,
  IonSpinner,
  IonDatetime,
  useIonToast
} from '@ionic/react';
import { add, chevronForward, calendarOutline, lockClosedOutline, lockOpenOutline, chevronBack, chevronForwardOutline, close, time, create, list, stop, starOutline } from 'ionicons/icons';
import './TimeRegistrationV2.css';
import EnvironmentBadge from '../components/EnvironmentBadge';
import getApi from '../api';
import { useLocalStorage } from '../components/useLocalStorage';

// Förslag-tabben och projekt/favoritlogik borttagen

interface DayHours {
  date: string;
  day: string;
  hours: number;
  isLocked: boolean;
  isHoliday: boolean;
}

interface TimeEntry {
  id: number;
  date: string;
  check_in: string;
  check_out: string | null;
  hours: number;
  desc: string;
  order_id: string;
  customer_name?: string | null;
  breaks?: number;
  comment_absense?: string | null;
}

interface TimeSummaryRegistration {
  id: number;
  check_in: string;
  check_out: string;
  hours: number;
  order_id: string;
  customer_name: string | null;
  desc: string;
  breaks: number;
  comment_absense?: string | null;
}

interface TimeSummaryDay {
  date: string;
  total_hours: number;
  sick: boolean;
  free: boolean;
  traktamente: string | null;
  registrations: TimeSummaryRegistration[];
}

interface TimeSummary {
  year: number;
  month: number;
  user: {
    id: number;
    name: string;
    email: string;
  };
  summary: {
    total_hours: number;
    total_days: number;
    work_days: number;
    sick_days: number;
    free_days: number;
  };
  days: TimeSummaryDay[];
}

const TimeRegistrationV2: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('projekt');
  const [me] = useLocalStorage("me", "");
  const router = useIonRouter();
  const api = getApi();
  const [presentAlert] = useIonAlert();
  const [presentToast] = useIonToast();

  // Modal states
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [registrationType, setRegistrationType] = useState<'stampelklocka' | 'registrera' | null>(null);
  
  // Customer search states
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customers, setCustomers] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [customerOrders, setCustomerOrders] = useState<any[]>([]);
  // Separata loading-states för tydligare UX
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  // Ref för debounce-timer + klick-race guard
  const searchDebounceRef = useRef<number | null>(null);
  const isSelectingCustomerRef = useRef(false);
  
  // Dagens unika ordrar (för snabbtillgång)
  const [todayUniqueOrders, setTodayUniqueOrders] = useState<Array<{orderId: string, customerName: string, customerId: string}>>([]);
  
  // Internal time categories
  const [internalTimeCategories, setInternalTimeCategories] = useState<any[]>([]);
  const [isLoadingInternalCategories, setIsLoadingInternalCategories] = useState(false);

  // Active time registration
  const [activeRegistration, setActiveRegistration] = useState<any | null>(null);
  const [elapsedTime, setElapsedTime] = useState<string>('00:00:00');
  const [isStoppingRegistration, setIsStoppingRegistration] = useState(false);

  // Today's registrations
  const [todayRegistrations, setTodayRegistrations] = useState<TimeEntry[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(''); // Vald dag för visning av registreringar

  // Week overview state
  const [currentWeek, setCurrentWeek] = useState<number>(0);
  const [currentYear, setCurrentYear] = useState<number>(0);
  const [weekHours, setWeekHours] = useState<DayHours[]>([]);
  const [totalWeekHours, setTotalWeekHours] = useState<number>(0);
  const [targetWeekHours, setTargetWeekHours] = useState<number>(40);
  
  // Cache för att lagra timesummary data per månad
  const [timeSummaryCache, setTimeSummaryCache] = useState<{ [key: string]: TimeSummary }>({});

  // Initiera vecka baserat på senast vald dag (persistens) annars dagens datum
  useEffect(() => {
    const storedDate = localStorage.getItem('trv_selectedDate');
    if (storedDate) {
      const parts = storedDate.split('-').map(Number);
      if (parts.length === 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
        const dateObj = new Date(parts[0], parts[1]-1, parts[2]);
        const week = getWeekNumber(dateObj);
        const year = dateObj.getFullYear();
        setCurrentWeek(week);
        setCurrentYear(year);
        setSelectedDate(storedDate);
        return;
      }
    }
    // Om ingen sparad datum finns, använd dagens datum
    const today = new Date();
    const week = getWeekNumber(today);
    const year = today.getFullYear();
    const todayStr = today.toISOString().split('T')[0];
    setCurrentWeek(week);
    setCurrentYear(year);
    setSelectedDate(todayStr);
  }, []);

  useEffect(() => {
    // Ladda veckodata när vecka/år ändras (vänta tills båda är satta)
    if (currentWeek > 0 && currentYear > 0 && me.id) {
      loadWeekData();
    }
  }, [currentWeek, currentYear, me.id]);

  // Ladda aktiv tidsregistrering vid sidladdning
  useEffect(() => {
    if (me.id) {
      loadActiveRegistration();
    }
  }, [me.id]);

  // Ladda intern tid-kategorier vid sidladdning
  useEffect(() => {
    loadInternalTimeCategories();
  }, []);

  // Lyssna på uppdateringar från redigeringsvyn
  useEffect(() => {
    const handleUpdate = () => {
      // Ladda om all data när en registrering uppdateras
      loadActiveRegistration();
      loadWeekData(true); // Tvinga refresh av data
    };

    window.addEventListener('timeRegistrationUpdated', handleUpdate);
    return () => window.removeEventListener('timeRegistrationUpdated', handleUpdate);
  }, [currentWeek, currentYear, selectedDate, me.id]);

  // Ladda dagens registreringar när modalen öppnas
  useEffect(() => {
    if (isProjectModalOpen && selectedDate) {
      loadRegistrationsForDate(selectedDate);
    }
  }, [isProjectModalOpen]);

  // Timer för att uppdatera elapsed time varje sekund
  useEffect(() => {
    if (!activeRegistration) return;

    const updateElapsedTime = () => {
      const checkIn = new Date(`${activeRegistration.date}T${activeRegistration.check_in}`);
      const now = new Date();
      const diffMs = now.getTime() - checkIn.getTime();
      
      // Hantera både positiva och negativa skillnader (om man går över midnatt)
      const absDiffMs = Math.abs(diffMs);
      const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
      const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((absDiffMs % (1000 * 60)) / 1000);
      
      const formatted = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      setElapsedTime(formatted);
    };

    updateElapsedTime(); // Kör direkt
    const timer = setInterval(updateElapsedTime, 1000);

    return () => clearInterval(timer);
  }, [activeRegistration]);

  const loadActiveRegistration = async () => {
    try {
      const response = await api.get('/api/bbe/active-time-registration/');
      if (response.data && response.data.id) {
        setActiveRegistration(response.data);
      } else {
        setActiveRegistration(null);
      }
    } catch (error: any) {
      // 404 betyder att det inte finns någon aktiv registrering - det är normalt
      if (error.response && error.response.status === 404) {
        setActiveRegistration(null);
      } else {
        console.error('Failed to load active registration:', error);
        setActiveRegistration(null);
      }
    }
  };

  const loadInternalTimeCategories = async () => {
    setIsLoadingInternalCategories(true);
    try {
      const response = await api.get('/user/internal-time-categories/');
      if (response.data && response.data.categories) {
        // Sortera efter sort_order och filtrera aktiva
        const activeCategories = response.data.categories
          .filter((cat: any) => cat.active !== false)
          .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
        setInternalTimeCategories(activeCategories);
      } else if (Array.isArray(response.data)) {
        // Stöd för enkel array-format
        setInternalTimeCategories(response.data.map((name: string, index: number) => ({
          id: index,
          name: name,
          active: true
        })));
      }
    } catch (error) {
      console.error('Failed to load internal time categories:', error);
      // Fallback till hårdkodade värden om API:et inte svarar
      setInternalTimeCategories([
        { id: 1, name: 'Administration', active: true },
        { id: 2, name: 'Drift', active: true },
        { id: 3, name: 'Intern Restid', active: true },
        { id: 4, name: 'Overksam', active: true },
        { id: 5, name: 'Utbildning', active: true },
        { id: 6, name: 'Veckomöte', active: true },
        { id: 7, name: 'Övrigt internmöte', active: true }
      ]);
    } finally {
      setIsLoadingInternalCategories(false);
    }
  };

  const handleStopActiveRegistration = async () => {
    if (!activeRegistration) return;
    // Om det är intern tid (saknar order_id) krävs en kommentar innan utstämpling
    if (!activeRegistration.order_id) {
      presentAlert({
        header: 'Avsluta intern tid',
        message: 'Ange en kommentar (obligatorisk)',
        inputs: [
          {
            name: 'comment',
            type: 'text',
            placeholder: 'Kommentar',
          }
        ],
        buttons: [
          { text: 'Avbryt', role: 'cancel' },
          {
            text: 'Spara & stoppa',
            handler: async (data) => {
              const comment = (data?.comment || '').trim();
              if (!comment) {
                // Return false för att hindra att alert stängs om tom kommentar
                return false;
              }
              setIsStoppingRegistration(true);
              const descCombined = `${activeRegistration.customer_name || 'Intern tid'} - ${comment}`;
              const formData = new FormData();
              formData.append('desc', descCombined);
              formData.append('order_id', '');
              try {
                const response = await api.post('/bbe/staff/check_in/', formData);
                console.log('Check out internal time response:', response.data);
                await loadActiveRegistration();
                await loadWeekData(true);
                const todayStr = new Date().toISOString().split('T')[0];
                if (selectedDate !== todayStr) {
                  setSelectedDate(todayStr);
                }
              } catch (error: any) {
                console.error('Failed to check out internal time:', error);
                presentAlert({
                  header: 'Fel',
                  message: error?.response?.data?.ErrorInformation?.Message || 'Det gick inte att stämpla ut intern tid.',
                  buttons: ['OK']
                });
              } finally {
                setIsStoppingRegistration(false);
              }
            }
          }
        ]
      });
      return;
    }

    // Vanlig order-baserad utstämpling
    setIsStoppingRegistration(true);
    const data = new FormData();
    data.append('desc', activeRegistration.desc || '');
    data.append('order_id', activeRegistration.order_id || '');
    try {
      const response = await api.post('/bbe/staff/check_in/', data);
      console.log('Check out response:', response.data);
      await loadActiveRegistration();
      await loadWeekData(true);
      const todayStr = new Date().toISOString().split('T')[0];
      if (selectedDate !== todayStr) {
        setSelectedDate(todayStr);
      }
    } catch (error: any) {
      console.error('Failed to check out:', error);
      presentAlert({
        header: 'Fel',
        message: error?.response?.data?.ErrorInformation?.Message || 'Det gick inte att stämpla ut.',
        buttons: ['OK']
      });
    } finally {
      setIsStoppingRegistration(false);
    }
  };

  const performCustomerSearch = () => {
    const trimmed = customerSearchQuery.trim();
    if (trimmed.length < 2) {
      setCustomers([]);
      setIsSearchingCustomers(false);
      return;
    }
    setIsSearchingCustomers(true);
    api.get(`/fortnox/customers/?search=${encodeURIComponent(trimmed)}`)
      .then(res => {
        if (res.data && res.data.Customers) {
          setCustomers(res.data.Customers);
        } else {
          setCustomers([]);
        }
        setIsSearchingCustomers(false);
      })
      .catch(err => {
        console.error(err);
        setIsSearchingCustomers(false);
        setCustomers([]);
      });
  };

  // Debounce kundsökning – ingen blur-sök för att undvika race vid första klick
  useEffect(() => {
    if (selectedCustomer) return; // Avbryt sök när kund är vald
    const trimmed = customerSearchQuery.trim();
    if (trimmed.length < 2) {
      setCustomers([]);
      setIsSearchingCustomers(false);
      return;
    }
    // Starta "Söker..." direkt
    setIsSearchingCustomers(true);
    // Rensa tidigare timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = window.setTimeout(() => {
      performCustomerSearch();
      searchDebounceRef.current = null;
    }, 300); // lite snabbare
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [customerSearchQuery, selectedCustomer]);

  const handleCustomerSelect = (customer: any) => {
    isSelectingCustomerRef.current = true; // Skydda mot blur-race
    // Avbryt eventuell väntande sök
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setSelectedCustomer(customer);
    setCustomers([]); // Rensa lista
    setIsSearchingCustomers(false);
    setIsLoadingOrders(true);
    api.get(`/fortnox/orders/?customernumber=${customer.CustomerNumber}&sent=False`)
      .then(res => {
        setCustomerOrders(res.data.Orders || []);
        setIsLoadingOrders(false);
        isSelectingCustomerRef.current = false;
      })
      .catch(err => {
        console.error(err);
        setCustomerOrders([]);
        setIsLoadingOrders(false);
        isSelectingCustomerRef.current = false;
      });
  };

  useIonViewDidEnter(() => {
    // Ladda aktiv tidsregistrering när användaren kommer tillbaka
    if (me.id) {
      loadActiveRegistration();
    }
    
    // Synka vecka med lagrad vald dag vid återkomst
    const storedDate = localStorage.getItem('trv_selectedDate');
    if (storedDate) {
      const [y,m,d] = storedDate.split('-').map(Number);
      if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
        const dateObj = new Date(y, m-1, d);
        const wk = getWeekNumber(dateObj);
        if (wk !== currentWeek || dateObj.getFullYear() !== currentYear) {
          setCurrentWeek(wk);
          setCurrentYear(dateObj.getFullYear());
        }
        setSelectedDate(storedDate);
      }
    }
    // loadWeekData() kommer att anropas automatiskt av useEffect när currentWeek/currentYear uppdateras
  });

  useIonViewWillLeave(() => {
    // Rensa sparat datum när användaren lämnar sidan
    // Nästa gång visas dagens datum som standard
    localStorage.removeItem('trv_selectedDate');
  });

  const loadRegistrationsForDate = async (dateStr: string, forceRefresh: boolean = false) => {
    if (!me.id) return;

    // Parse date som YYYY-MM-DD utan timezone-problem
    const [year, month, day] = dateStr.split('-').map(Number);
    const cacheKey = `${year}-${month}`;

    try {
      let timeSummary: TimeSummary | null = null;
      
      // Kolla om vi har cachen (skippa om forceRefresh)
      if (!forceRefresh && timeSummaryCache[cacheKey]) {
        timeSummary = timeSummaryCache[cacheKey];
      } else {
        // Hämta data från det nya API:t
        const response = await api.get(`/user/timesummary/?year=${year}&month=${month}`);
        
        if (response.data) {
          timeSummary = response.data;
          
          // Spara i cache
          setTimeSummaryCache(prev => ({
            ...prev,
            [cacheKey]: timeSummary!
          }));
        }
      }
      
      if (timeSummary && timeSummary.days) {
        // Hitta datumet i responsen
        const dayData = timeSummary.days.find(d => d.date === dateStr);
        
        if (dayData && dayData.registrations) {
          // Konvertera till TimeEntry format
          const entries: TimeEntry[] = dayData.registrations.map((reg) => ({
            id: reg.id, // Använd verkligt ID från backend
            date: dateStr,
            check_in: reg.check_in,
            check_out: reg.check_out,
            hours: reg.hours,
            desc: reg.desc,
            order_id: reg.order_id,
            customer_name: reg.customer_name,
            breaks: reg.breaks,
            comment_absense: reg.comment_absense
          }));
          
          setTodayRegistrations(entries);
          
          // Extrahera unika ordrar från dagens registreringar
          const uniqueOrdersMap = new Map<string, {orderId: string, customerName: string, customerId: string}>();
          entries.forEach(entry => {
            if (entry.order_id && !uniqueOrdersMap.has(entry.order_id)) {
              uniqueOrdersMap.set(entry.order_id, {
                orderId: entry.order_id,
                customerName: entry.customer_name || '',
                customerId: '' // customerId finns inte i nuvarande data, men kan läggas till senare
              });
            }
          });
          setTodayUniqueOrders(Array.from(uniqueOrdersMap.values()));
        } else {
          setTodayRegistrations([]);
          setTodayUniqueOrders([]);
        }
      }
    } catch (error) {
      console.error('Failed to load registrations for date:', error);
      setTodayRegistrations([]);
    }
  };

  const handleDayClick = (dateStr: string) => {
    setSelectedDate(dateStr);
    localStorage.setItem('trv_selectedDate', dateStr);
    loadRegistrationsForDate(dateStr);
  };

  const getDateString = (dateStr: string): string => {
    // Parse date som YYYY-MM-DD utan timezone-problem
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    const days = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
    
    // Konvertera JavaScript's getDay() (0=söndag) till ISO 8601 (0=måndag)
    let dayIndex = date.getDay() - 1;
    if (dayIndex < 0) dayIndex = 6; // Söndag blir 6
    
    const dayName = days[dayIndex];
    const dayNum = date.getDate();
    const monthName = months[date.getMonth()];
    
    return `${dayName} ${dayNum} ${monthName}`;
  };

  const getWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  const getWeekDates = (week: number, year: number): Date[] => {
    // ISO 8601: Hitta måndagen i den angivna veckan
    // Vecka 1 är veckan som innehåller första torsdagen (4 jan är alltid i vecka 1)
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Day = jan4.getUTCDay() || 7; // 1=Mån...7=Sön
    // Måndagen i vecka 1
    const week1Monday = new Date(jan4);
    week1Monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1);
    // Måndagen i önskad vecka
    const targetMonday = new Date(week1Monday);
    targetMonday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
    // Generera 7 datum från måndag
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(targetMonday);
      d.setUTCDate(targetMonday.getUTCDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const loadWeekData = async (forceRefresh: boolean = false) => {
    if (!me.id) return;

    try {
      // Hämta veckans datum
      const weekDates = getWeekDates(currentWeek, currentYear);
      
      // Hitta vilka månader som ingår i veckan
      const monthsToFetch = new Set<string>();
      weekDates.forEach(date => {
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth() + 1;
        monthsToFetch.add(`${year}-${month}`);
      });

      // Hämta data för alla relevanta månader
      const allDaysData: TimeSummaryDay[] = [];
      
      for (const yearMonth of Array.from(monthsToFetch)) {
        const [year, month] = yearMonth.split('-').map(Number);
        const cacheKey = `${year}-${month}`;
        
        // Kolla om vi redan har cachen (skippa cache om forceRefresh är true)
        if (!forceRefresh && timeSummaryCache[cacheKey]) {
          allDaysData.push(...timeSummaryCache[cacheKey].days);
        } else {
          // Hämta från API
          const response = await api.get(`/user/timesummary/?year=${year}&month=${month}`);
          
          if (response.data && response.data.days) {
            const timeSummary: TimeSummary = response.data;
            allDaysData.push(...timeSummary.days);
            
            // Spara i cache
            setTimeSummaryCache(prev => ({
              ...prev,
              [cacheKey]: timeSummary
            }));
          }
        }
      }
      
      // Konvertera till weekHours format
      const dayNames = ['MÅN', 'TIS', 'ONS', 'TOR', 'FRE', 'LÖR', 'SÖN'];
      
      const weekData: DayHours[] = weekDates.map((date, index) => {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        const dayNum = date.getUTCDate();
        
        // Hitta matchande dag från API:t
        const dayData = allDaysData.find(d => d.date === dateStr);
        
        // Beräkna totala timmar minus rast
        let effectiveHours = 0;
        if (dayData) {
          // Räkna bort rast från varje registrering
          const totalBreaksInMinutes = dayData.registrations?.reduce((sum, reg) => sum + (reg.breaks || 0), 0) || 0;
          effectiveHours = dayData.total_hours - (totalBreaksInMinutes / 60);
        }
        
        // Konvertera UTC-datum till lokal Date för helgdagskontroll
        const localDate = new Date(year, date.getUTCMonth(), dayNum);
        
        return {
          date: dateStr,
          day: `${dayNames[index]}\n${dayNum}`,
          hours: effectiveHours,
          isLocked: date < new Date(new Date().setDate(new Date().getDate() - 7)),
          isHoliday: isSwedishHoliday(localDate)
        };
      });

      setWeekHours(weekData);
      
      // Beräkna totalt
      const total = weekData.reduce((sum, day) => sum + day.hours, 0);
      setTotalWeekHours(total);
      
      // Ladda även dagens/valda dagens registreringar (skicka vidare forceRefresh)
      await loadRegistrationsForDate(selectedDate || new Date().toISOString().split('T')[0], forceRefresh);
    } catch (error) {
      console.error('Failed to load week data:', error);
    }
  };

  const changeWeek = (direction: number) => {
    let newWeek = currentWeek + direction;
    let newYear = currentYear;

    // Beräkna antal veckor i året (kan vara 52 eller 53)
    const getWeeksInYear = (year: number): number => {
      const dec31 = new Date(year, 11, 31);
      const weekNum = getWeekNumber(dec31);
      // Om 31 dec är i vecka 1, då har året 52 veckor (vecka 1 tillhör nästa år)
      return weekNum === 1 ? 52 : weekNum;
    };

    if (newWeek < 1) {
      newYear--;
      newWeek = getWeeksInYear(newYear);
    } else if (newWeek > getWeeksInYear(newYear)) {
      newWeek = 1;
      newYear++;
    }

    setCurrentWeek(newWeek);
    setCurrentYear(newYear);
  };

  const getMonthName = (week: number, year: number): string => {
    const weekDates = getWeekDates(week, year);
    const monthNames = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 
                        'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
    return monthNames[weekDates[3].getUTCMonth()]; // Använd onsdagen som referens
  };

  // Förslags-relaterade funktioner borttagna (favoriter, senaste, etc.)

  const handleOrderSelect = async (orderId: string, order: any) => {
    if (registrationType === 'stampelklocka') {
      // Starta stämpelklocka
      await startTimeClockRegistration(orderId, order);
    } else {
      // Registrera tid - navigera till formulär
      // Persistera vald dag och kundinformation innan navigering
      if (selectedDate) {
        localStorage.setItem('trv_selectedDate', selectedDate);
      }
      
      // Om vi har en vald kund från sökningen, använd den
      if (selectedCustomer) {
        localStorage.setItem('trv_customerName', selectedCustomer.Name || '');
        localStorage.setItem('trv_customerId', selectedCustomer.CustomerNumber || '');
      } else {
        // Annars, kolla om ordern finns i dagens ordrar (från senaste-fliken)
        const todayOrder = todayUniqueOrders.find(o => o.orderId === orderId);
        if (todayOrder && todayOrder.customerName) {
          localStorage.setItem('trv_customerName', todayOrder.customerName);
          localStorage.setItem('trv_customerId', todayOrder.customerId || '');
        }
      }
      
      router.push(`/TimeRegistrationV2/register/${orderId}`);
    }
    
    // Återställ kundsökning och stäng modalen
    setCustomerSearchQuery('');
    setCustomers([]);
    setSelectedCustomer(null);
    setCustomerOrders([]);
    setIsProjectModalOpen(false);
  };

  const handleCreateNewOrder = async () => {
    if (!selectedCustomer) return;
    
    setIsCreatingOrder(true);
    try {
      const response = await api.post('/fortnox/orders/new/', { id: selectedCustomer.CustomerNumber });
      if (response.status === 201 || response.status === 200) {
        presentToast({
          message: 'Ny order skapad!',
          duration: 3000,
          color: 'success',
          position: 'bottom'
        });
        
        // Ladda om ordrar för kunden
        setIsLoadingOrders(true);
        const res = await api.get(`/fortnox/orders/?customernumber=${selectedCustomer.CustomerNumber}&sent=False`);
        setCustomerOrders(res.data.Orders || []);
        setIsLoadingOrders(false);
      }
    } catch (error: any) {
      console.error('Failed to create new order:', error);
      presentAlert({
        header: 'Fel',
        message: error?.response?.data?.error || 'Det gick inte att skapa en ny order.',
        buttons: ['OK']
      });
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const startTimeClockRegistration = async (orderId: string, order: any) => {
    try {
      const now = new Date();
      
      // Hämta kundinformation - antingen från vald kund eller från dagens ordrar
      let customerName = '';
      let customerId = '';
      
      if (selectedCustomer) {
        customerName = selectedCustomer.Name || '';
        customerId = selectedCustomer.CustomerNumber || '';
      } else {
        const todayOrder = todayUniqueOrders.find(o => o.orderId === orderId);
        if (todayOrder) {
          customerName = todayOrder.customerName;
          customerId = todayOrder.customerId || '';
        }
      }
      
      const timeData = {
        date: now.toISOString().split('T')[0],
        check_in: now.toTimeString().split(' ')[0].substring(0, 8), // HH:MM:SS
        check_out: null,
        desc: order.YourOrderNumber || orderId,
        order_id: orderId,
        customer_name: customerName,
        customer_id: customerId,
        user: me.id,
        edited: "False"
      };

      const response = await api.post('/user/timeregister/', timeData);
      
      if (response.status === 201) {
        // Ladda om aktiv registrering för att visa bannern
        await loadActiveRegistration();
        
        // Ladda om veckodata och dagens registreringar (tvinga refresh)
        await loadWeekData(true);
        
        // Sätt selectedDate till dagens datum om det inte redan är satt
        const todayStr = now.toISOString().split('T')[0];
        if (selectedDate !== todayStr) {
          setSelectedDate(todayStr);
        }
      }
    } catch (error) {
      console.error('Failed to start time clock registration:', error);
    }
  };

  // Starta stämpelklocka för intern tid
  const startInternalTimeClockRegistration = async (internalName: string) => {
    try {
      const now = new Date();
      const timeData = {
        date: now.toISOString().split('T')[0],
        check_in: now.toTimeString().split(' ')[0].substring(0, 8),
        check_out: null,
        desc: internalName, // Initial beskrivning = intern tid-namn, kompletteras vid stopp
        order_id: '',
        customer_name: internalName,
        customer_id: '',
        user: me.id,
        edited: 'False'
      };
      const response = await api.post('/user/timeregister/', timeData);
      if (response.status === 201) {
        await loadActiveRegistration();
        await loadWeekData(true);
        const todayStr = now.toISOString().split('T')[0];
        if (selectedDate !== todayStr) {
          setSelectedDate(todayStr);
        }
      }
    } catch (error: any) {
      console.error('Failed to start internal time clock registration:', error);
      presentAlert({
        header: 'Fel',
        message: 'Det gick inte att starta intern tid stämpelklocka.',
        buttons: ['OK']
      });
    }
  };

  const handleChoiceSelect = (choice: 'stampelklocka' | 'registrera') => {
    setRegistrationType(choice);
    // Börja med senaste-fliken om det finns ordrar, annars projekt-fliken
    setActiveTab(todayUniqueOrders.length > 0 ? 'senaste' : 'projekt');
    setIsProjectModalOpen(true);
  };

  // Helper function to get weekdays between two dates
  const getWeekdaysBetween = (start: Date, end: Date): Date[] => {
    const dates: Date[] = [];
    const current = new Date(start);
    
    while (current <= end) {
      const dayOfWeek = current.getDay();
      // 0 = Sunday, 6 = Saturday
      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !isSwedishHoliday(current)) {
        dates.push(new Date(current));
      }
      current.setDate(current.getDate() + 1);
    }
    
    return dates;
  };

  // Check if a date is a Swedish holiday
  const isSwedishHoliday = (date: Date): boolean => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // 1-12
    const day = date.getDate();
    
    // Fast holidays
    const fixedHolidays = [
      { month: 1, day: 1 },   // Nyårsdagen
      { month: 1, day: 6 },   // Trettondedag jul
      { month: 5, day: 1 },   // Första maj
      { month: 6, day: 6 },   // Nationaldagen
      { month: 12, day: 24 }, // Julafton
      { month: 12, day: 25 }, // Juldagen
      { month: 12, day: 26 }, // Annandag jul
      { month: 12, day: 31 }, // Nyårsafton
    ];
    
    for (const holiday of fixedHolidays) {
      if (month === holiday.month && day === holiday.day) {
        return true;
      }
    }
    
    // Moveable holidays based on Easter (simplified calculation)
    const easter = getEasterDate(year);
    const easterTime = easter.getTime();
    const currentTime = date.getTime();
    const dayInMs = 24 * 60 * 60 * 1000;
    
    // Långfredagen (Good Friday) - 2 days before Easter
    if (currentTime === easterTime - 2 * dayInMs) return true;
    
    // Påskdagen (Easter Sunday)
    if (currentTime === easterTime) return true;
    
    // Annandag påsk (Easter Monday)
    if (currentTime === easterTime + dayInMs) return true;
    
    // Kristi himmelsfärdsdag (Ascension Day) - 39 days after Easter (always a Thursday)
    if (currentTime === easterTime + 39 * dayInMs) return true;
    
    // Pingstdagen (Whit Sunday) - 49 days after Easter
    if (currentTime === easterTime + 49 * dayInMs) return true;
    
    // Midsommarafton - Friday between June 19-25
    if (month === 6 && day >= 19 && day <= 25 && date.getDay() === 5) return true;
    
    // Midsommardagen - Saturday between June 20-26
    if (month === 6 && day >= 20 && day <= 26 && date.getDay() === 6) return true;
    
    // Alla helgons dag - Saturday between October 31 and November 6
    if ((month === 10 && day === 31 && date.getDay() === 6) || 
        (month === 11 && day <= 6 && date.getDay() === 6)) return true;
    
    return false;
  };

  // Calculate Easter date using Meeus/Jones/Butcher algorithm
  const getEasterDate = (year: number): Date => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    
    return new Date(year, month - 1, day);
  };

  const handleAbsenceSelect = (absenceType: string) => {
    presentAlert({
      header: `Registrera ${absenceType}`,
      inputs: [
        {
          name: 'startDate',
          type: 'date',
          placeholder: 'Startdatum',
          value: selectedDate || new Date().toISOString().split('T')[0]
        },
        {
          name: 'endDate',
          type: 'date',
          placeholder: 'Slutdatum',
          value: selectedDate || new Date().toISOString().split('T')[0]
        },
        {
          name: 'description',
          type: 'textarea',
          placeholder: 'Beskrivning (valfritt)',
        }
      ],
      buttons: [
        { text: 'Avbryt', role: 'cancel' },
        {
          text: 'Registrera',
          handler: async (data) => {
            if (!data.startDate || !data.endDate) {
              presentToast({
                message: 'Välj start- och slutdatum',
                duration: 3000,
                color: 'warning',
                position: 'bottom'
              });
              return false;
            }

            const startDate = new Date(data.startDate);
            const endDate = new Date(data.endDate);
            
            if (startDate > endDate) {
              presentToast({
                message: 'Startdatum måste vara före slutdatum',
                duration: 3000,
                color: 'warning',
                position: 'bottom'
              });
              return false;
            }

            const weekdays = getWeekdaysBetween(startDate, endDate);

            try {
              const promises = weekdays.map((date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const dateString = `${year}-${month}-${day}`;
                
                const timeData: any = {
                  date: dateString,
                  check_in: '08:00',
                  check_out: '16:00',
                  desc: data.description || absenceType,
                  hours: '8',
                  order_id: '0',
                  traktamente: '',
                  user: me.id,
                  edited: 'False',
                  comment_absense: absenceType  // Sätt titeln för alla frånvaro-typer
                };

                // Set appropriate flags based on absence type
                if (absenceType === 'Sjuk') {
                  timeData.sick = 'True';
                } else if (absenceType === 'Semester') {
                  timeData.free = 'True';
                } else {
                  // For other types: Föräldraledigt, Tjänstledigt, Vård av barn, Kompledigt
                  timeData.absense = 'True';
                }

                return api.post('/user/timeregister/', timeData);
              });

              await Promise.all(promises);

              presentToast({
                message: `${absenceType} registrerad för ${weekdays.length} vardag${weekdays.length > 1 ? 'ar' : ''}`,
                duration: 3000,
                color: 'success',
                position: 'bottom'
              });

              // Reload data and close modal
              await loadWeekData(true);
              setIsProjectModalOpen(false);
            } catch (error: any) {
              console.error('Failed to register absence:', error);
              presentToast({
                message: `Det gick inte att registrera ${absenceType.toLowerCase()}`,
                duration: 3000,
                color: 'danger',
                position: 'bottom'
              });
            }
          }
        }
      ]
    });
  };

  const handleInternalTimeSelect = (internalTimeName: string) => {
    // Persistera vald dag och interntid-namn innan navigering
    if (selectedDate) {
      localStorage.setItem('trv_selectedDate', selectedDate);
    }
    localStorage.setItem('trv_internalTimeName', internalTimeName);
    
    if (registrationType === 'stampelklocka') {
      // Starta stämpelklocka för intern tid
      startInternalTimeClockRegistration(internalTimeName);
      // Stäng modalen
      setIsProjectModalOpen(false);
    } else {
      // Navigera till manuellt formulär utan projectId (intern tid)
      router.push('/TimeRegistrationV2/register/internal');
      setIsProjectModalOpen(false);
    }
  };

  const handleRegistrationClick = (entry: TimeEntry) => {
    // Navigera till nya redigeringsvyn
    router.push(`/TimeRegistrationV2/edit/${entry.id}`);
  };

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonBackButton defaultHref="/Home" />
          </IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Registrera tid</IonTitle>
          <div slot="end" style={{paddingRight:'8px'}}>
            <EnvironmentBadge />
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent className="time-registration-v2" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        {/* Active Time Registration */}
        {activeRegistration && (
          <IonCard 
            className="active-registration-card"
            style={{
              backgroundColor: '#e8f5e9',
              margin: '16px',
              marginBottom: '8px'
            }}
            placeholder={undefined} 
            onPointerEnterCapture={undefined} 
            onPointerLeaveCapture={undefined}
          >
            <IonCardContent 
              style={{ padding: '12px' }}
              placeholder={undefined} 
              onPointerEnterCapture={undefined} 
              onPointerLeaveCapture={undefined}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: 'bold', 
                    color: 'var(--ion-color-primary)',
                    marginBottom: '4px'
                  }}>
                    {elapsedTime}
                  </div>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: '600',
                    marginBottom: '2px'
                  }}>
                    {activeRegistration.customer_name || 'Interntid'}
                  </div>
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#666'
                  }}>
                    {activeRegistration.order_id ? `Ordernummer: ${activeRegistration.order_id}` : (activeRegistration.desc || 'Ingen beskrivning')}
                  </div>
                </div>
                <IonButton
                  shape="round"
                  fill="solid"
                  color="danger"
                  style={{
                    width: '64px',
                    height: '64px',
                    '--border-radius': '50%',
                    '--box-shadow': '0 2px 8px rgba(0,0,0,0.3)',
                    '--padding-start': '0',
                    '--padding-end': '0'
                  }}
                  onClick={handleStopActiveRegistration}
                  placeholder={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                >
                  <IonIcon 
                    icon={stop} 
                    style={{ fontSize: '48px' }}
                    placeholder={undefined} 
                    onPointerEnterCapture={undefined} 
                    onPointerLeaveCapture={undefined} 
                  />
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        )}

        {/* Week Overview */}
        <IonCard className="week-overview-card" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {/* Week selector */}
            <div className="week-selector">
              <IonButton fill="clear" onClick={() => changeWeek(-1)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonIcon icon={chevronBack} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              </IonButton>
              <div className="week-info">
                <IonIcon icon={calendarOutline} className="calendar-icon" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                <span className="month-text">{getMonthName(currentWeek, currentYear)}, v{currentWeek}</span>
                <IonButton fill="clear" size="small" className="dropdown-btn" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  {/* <IonIcon icon={chevronForwardOutline} style={{transform: 'rotate(90deg)'}} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} /> */}
                </IonButton>
              </div>
              <IonButton fill="clear" onClick={() => changeWeek(1)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonIcon icon={chevronForwardOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              </IonButton>
            </div>

            {/* Days of week */}
            <div className="week-days">
              {weekHours.map((day, index) => (
                <div 
                  key={index} 
                  className={`day-column ${day.hours > 0 ? 'has-hours' : ''} ${day.date === selectedDate ? 'selected' : ''} ${day.isHoliday ? 'holiday' : ''}`}
                  onClick={() => handleDayClick(day.date)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="day-label" style={day.isHoliday ? { color: '#d32f2f' } : {}}>{day.day}</div>
                  <div className="day-hours">{day.hours.toFixed(2)}</div>
                </div>
              ))}
            </div>

            {/* Weekly summary */}
            <div className="week-summary">
              <div className="summary-text">
                {totalWeekHours.toFixed(2)} h / {targetWeekHours.toFixed(2)} h
              </div>
            </div>

            {/* Add articles button - only show when there's an active registration on an order */}
            {activeRegistration && activeRegistration.order_id && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e0e0e0' }}>
                <IonButton 
                  expand="block" 
                  color="tertiary"
                  onClick={() => router.push(`/order/${activeRegistration.order_id}`)}
                  placeholder={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                >
                  <IonIcon slot="start" icon={add} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                  Lägg till artiklar på order {activeRegistration.order_id}
                </IonButton>
              </div>
            )}

            {/* Time clock button - only show when there's no active registration */}
            {!activeRegistration && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e0e0e0' }}>
                <IonButton 
                  expand="block" 
                  color="success"
                  onClick={() => handleChoiceSelect('stampelklocka')}
                  placeholder={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                >
                  <IonIcon slot="start" icon={time} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                  Starta stämpelklocka
                </IonButton>
              </div>
            )}
          </IonCardContent>
        </IonCard>

        {/* Today's Registrations */}
        <div className="registrations-section">
          <div className="section-header">
            <IonIcon icon={calendarOutline} className="header-icon" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            <h2 className="section-title-main">{getDateString(selectedDate || new Date().toISOString().split('T')[0])}</h2>
          </div>

          {todayRegistrations.length > 0 ? (
            <IonList className="registrations-list" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              {todayRegistrations.map(entry => {
                const hours = typeof entry.hours === 'number' ? entry.hours : parseFloat(entry.hours || '0');
                const breakMinutes = entry.breaks || 0;
                // Räkna bort rast från totala timmar (rast är i minuter, konvertera till timmar)
                const effectiveHours = hours - (breakMinutes / 60);
                
                // Använd comment_absense som displayName om det finns (frånvaro), annars customer_name/order_id
                const displayName = entry.comment_absense || entry.customer_name || entry.order_id || 'Intern tid';
                
                return (
                <IonCard 
                  key={entry.id} 
                  button
                  onClick={() => handleRegistrationClick(entry)}
                  className="registration-card" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                >
                  <IonCardHeader className="registration-header" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <div className="registration-time">
                      {entry.check_in} - {entry.check_out || 'Pågår'}
                    </div>
                    <div className="registration-hours">
                      {effectiveHours.toFixed(2)} h
                    </div>
                  </IonCardHeader>
                  <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <div className="registration-project">
                      <strong>{displayName}</strong>
                      {entry.order_id && entry.customer_name && !entry.comment_absense && (
                        <span className="order-id"> (Order: {entry.order_id})</span>
                      )}
                    </div>
                    <div className="registration-desc">
                      {entry.desc}
                    </div>
                    {breakMinutes > 0 && (
                      <div className="registration-breaks">
                        Rast: {breakMinutes} min
                      </div>
                    )}
                  </IonCardContent>
                </IonCard>
                );
              })}
            </IonList>
          ) : (
            <div className="no-registrations">
              <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <p>Inga registreringar för idag</p>
              </IonText>
            </div>
          )}
        </div>

        {/* Floating Action Button - only for manual time registration, hidden when there's an active registration */}
        {!activeRegistration && (
          <IonFab vertical="bottom" horizontal="end" slot="fixed" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonFabButton 
              color="success"
              onClick={() => handleChoiceSelect('registrera')}
              placeholder={undefined} 
              onPointerEnterCapture={undefined} 
              onPointerLeaveCapture={undefined}
            >
              <IonIcon icon={add} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            </IonFabButton>
          </IonFab>
        )}

        {/* Project Selection Modal */}
        <IonModal 
          isOpen={isProjectModalOpen} 
          onDidDismiss={() => setIsProjectModalOpen(false)}
          className="project-modal" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}        >
          <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Skapa tidsregistrering</IonTitle>
                <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonButton onClick={() => setIsProjectModalOpen(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonIcon icon={close} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>

            <IonContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <div className="tabs-container">
                <IonSegment value={activeTab} onIonChange={e => setActiveTab(e.detail.value as string)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  {todayUniqueOrders.length > 0 && (
                    <IonSegmentButton value="senaste" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Senaste</IonLabel>
                    </IonSegmentButton>
                  )}
                  <IonSegmentButton value="projekt" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Order</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="interntid" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Interntid</IonLabel>
                  </IonSegmentButton>
                  {registrationType !== 'stampelklocka' && (
                    <IonSegmentButton value="franvaro" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Frånvaro</IonLabel>
                    </IonSegmentButton>
                  )}
                </IonSegment>
              </div>


              {activeTab === 'senaste' && (
                <div className="content-container">
                  {todayUniqueOrders.length > 0 ? (
                    <>
                      <div style={{ padding: '16px' }}>
                        <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <p>Ordrar du registrerat tid på idag ({selectedDate}):</p>
                        </IonText>
                      </div>
                      <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        {todayUniqueOrders.map(order => (
                          <IonItem 
                            key={order.orderId} 
                            button 
                            onClick={() => handleOrderSelect(order.orderId, { YourOrderNumber: order.orderId })}
                            placeholder={undefined}
                            onPointerEnterCapture={undefined}
                            onPointerLeaveCapture={undefined}
                          >
                            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                              <h2>{order.customerName || 'Okänd kund'}</h2>
                              <p>Order: {order.orderId}</p>
                            </IonLabel>
                          </IonItem>
                        ))}
                      </IonList>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <p>Du har inte registrerat tid på några ordrar idag ännu.</p>
                        <p style={{ marginTop: '8px' }}>Gå till "Order"-fliken för att söka efter en order.</p>
                      </IonText>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'projekt' && (
                <div className="content-container">
                  {!selectedCustomer ? (
                    <>
                      <div style={{ padding: '16px 16px 0 16px' }}>
                        <IonSearchbar
                          value={customerSearchQuery}
                          onIonChange={(e: any) => {
                            const value = e.detail.value;
                            setCustomerSearchQuery(value === null || value === undefined ? '' : value);
                          }}
                          onKeyDown={(e: any) => {
                            if (e.key === 'Enter') {
                              performCustomerSearch();
                            }
                          }}
                          // Ingen blur-sök för att undvika klick-race
                          placeholder="Sök på kundnamn eller nummer"
                          animated={true}
                          debounce={0} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                        />
                      </div>
                      {isSearchingCustomers && (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Söker...</IonText>
                        </div>
                      )}
                      {customers.length > 0 && (
                        <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          {customers.map(customer => (
                            <IonItem
                              key={customer.CustomerNumber}
                              button
                              onClick={() => handleCustomerSelect(customer)}
                              placeholder={undefined}
                              onPointerEnterCapture={undefined}
                              onPointerLeaveCapture={undefined}
                            >
                              <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <h2>{customer.Name}</h2>
                                <p>Kundnummer: {customer.CustomerNumber}</p>
                              </IonLabel>
                            </IonItem>
                          ))}
                        </IonList>
                      )}
                      {customerSearchQuery.length > 0 && customerSearchQuery.trim().length < 2 && (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <p>Skriv minst 2 tecken för att söka</p>
                          </IonText>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div style={{ padding: '16px' }}>
                        <IonButton 
                          fill="clear" 
                          onClick={() => {
                            setSelectedCustomer(null);
                            setCustomerOrders([]);
                          }}
                          placeholder={undefined} 
                          onPointerEnterCapture={undefined} 
                          onPointerLeaveCapture={undefined}
                        >
                          ← Tillbaka till kundsökning
                        </IonButton>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', marginBottom: '16px' }}>
                          <h3 style={{ margin: 0 }}>
                            Ordrar för {selectedCustomer.Name}
                          </h3>
                          <IonButton 
                            fill="outline" 
                            size="small" 
                            color="success"
                            disabled={isCreatingOrder}
                            onClick={handleCreateNewOrder}
                            placeholder={undefined} 
                            onPointerEnterCapture={undefined} 
                            onPointerLeaveCapture={undefined}
                          >
                            {isCreatingOrder ? (
                              <IonSpinner name="crescent" slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                            ) : (
                              <IonIcon icon={add} slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                            )}
                            Skapa ny order
                          </IonButton>
                        </div>
                      </div>
                      {isLoadingOrders && (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Laddar ordrar...</IonText>
                        </div>
                      )}
                      {customerOrders.length > 0 ? (
                        <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          {customerOrders
                            .filter((order: any) => !order.Cancelled)
                            .map((order: any) => (
                            <IonItem 
                              key={order.DocumentNumber} 
                              button 
                              onClick={() => handleOrderSelect(order.DocumentNumber, order)}
                              placeholder={undefined} 
                              onPointerEnterCapture={undefined} 
                              onPointerLeaveCapture={undefined}
                            >
                              <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <h2>Order: {order.DocumentNumber}</h2>
                                <p>{order.OrderDate}</p>
                              </IonLabel>
                            </IonItem>
                          ))}
                        </IonList>
                      ) : !isLoadingOrders && (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <p>Inga öppna ordrar hittades för denna kund</p>
                          </IonText>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'interntid' && (
                <div className="content-container">
                  <div style={{ padding: '16px' }}>
                    {isLoadingInternalCategories ? (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <IonSpinner name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                        <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <p>Laddar kategorier...</p>
                        </IonText>
                      </div>
                    ) : (
                      internalTimeCategories.map((category: any) => (
                        <IonCard 
                          key={category.id || category.name}
                          button 
                          onClick={() => handleInternalTimeSelect(category.name)}
                          style={{ margin: '0 0 16px 0' }}
                          placeholder={undefined}
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        >
                          <IonCardContent style={{ padding: '16px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonText color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                              <h3 style={{ margin: 0, fontSize: '16px' }}>{category.name}</h3>
                            </IonText>
                            {category.description && (
                              <IonText color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>{category.description}</p>
                              </IonText>
                            )}
                          </IonCardContent>
                        </IonCard>
                      ))
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'franvaro' && (
                <div className="content-container">
                  <div style={{ padding: '16px' }}>
                    <IonCard 
                      button 
                      onClick={() => handleAbsenceSelect('Föräldraledigt')}
                      style={{ margin: '0 0 16px 0' }}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      <IonCardContent style={{ padding: '16px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonText color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <h3 style={{ margin: 0, fontSize: '16px' }}>Föräldraledigt</h3>
                        </IonText>
                      </IonCardContent>
                    </IonCard>
                    
                    <IonCard 
                      button 
                      onClick={() => handleAbsenceSelect('Semester')}
                      style={{ margin: '0 0 16px 0' }}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      <IonCardContent style={{ padding: '16px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonText color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <h3 style={{ margin: 0, fontSize: '16px' }}>Semester</h3>
                        </IonText>
                      </IonCardContent>
                    </IonCard>
                    
                    <IonCard 
                      button 
                      onClick={() => handleAbsenceSelect('Sjuk')}
                      style={{ margin: '0 0 16px 0' }}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      <IonCardContent style={{ padding: '16px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonText color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <h3 style={{ margin: 0, fontSize: '16px' }}>Sjuk</h3>
                        </IonText>
                      </IonCardContent>
                    </IonCard>
                    
                    <IonCard 
                      button 
                      onClick={() => handleAbsenceSelect('Tjänstledigt')}
                      style={{ margin: '0 0 16px 0' }}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      <IonCardContent style={{ padding: '16px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonText color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <h3 style={{ margin: 0, fontSize: '16px' }}>Tjänstledigt</h3>
                        </IonText>
                      </IonCardContent>
                    </IonCard>
                    
                    <IonCard 
                      button 
                      onClick={() => handleAbsenceSelect('Vård av barn')}
                      style={{ margin: '0 0 16px 0' }}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      <IonCardContent style={{ padding: '16px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonText color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <h3 style={{ margin: 0, fontSize: '16px' }}>Vård av barn</h3>
                        </IonText>
                      </IonCardContent>
                    </IonCard>
                    
                    <IonCard 
                      button 
                      onClick={() => handleAbsenceSelect('Kompledigt')}
                      style={{ margin: '0 0 16px 0' }}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      <IonCardContent style={{ padding: '16px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonText color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <h3 style={{ margin: 0, fontSize: '16px' }}>Kompledigt</h3>
                        </IonText>
                      </IonCardContent>
                    </IonCard>
                  </div>
                </div>
              )}
            </IonContent>
          </IonPage>
        </IonModal>
      </IonContent>

      {/* Loading spinner när stämpelklocka stoppas */}
      <IonLoading
        isOpen={isStoppingRegistration}
        message="Stoppar stämpelklocka..."
      />
    </IonPage>
  );
};

export default TimeRegistrationV2;
